import { Ref, ref, watch } from 'vue';

/**
 * Ref mirrored in `localStorage`, so the value survives a page reload. The stored value is read
 * synchronously when the composable runs, so the first render already uses it.
 *
 * Reads and writes are guarded because storage access throws in private browsing or when the user
 * blocks site data.
 *
 * @example
 * const isCollapsed = useLocalStorageRef('customily.sidebar.collapsed', false);
 */
export const useLocalStorageRef = <T>(key: string, defaultValue: T): Ref<T> => {
    const read = (): T => {
        try {
            const raw = localStorage.getItem(key);
            return raw === null ? defaultValue : (JSON.parse(raw) as T);
        } catch {
            return defaultValue;
        }
    };

    const state = ref(read()) as Ref<T>;

    watch(state, (value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Storage unavailable: keep working in memory only
        }
    });

    return state;
};
